import L from "leaflet";
import { mergePublicPresence } from "./city-day-presentation";
import type { TimelineItem } from "./city-day-timeline";

type Artwork = {
  vectorUrl?: string | null;
  previewUrl?: string | null;
  cardUrl?: string | null;
};
export type MapPlace = {
  id: string;
  name: string;
  fullName: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  selectable?: boolean;
  pinImage?: Artwork | null;
};
export type PublicMapContext = {
  timeZone: string;
  timeline?: TimelineItem[];
  timelineState?: "current" | "stale";
  stayMergeGapMinutes?: number;
};

/** Both CityDay entry points draw only the public projection supplied by the API. */
export function drawCityDayMap(
  map: L.Map,
  places: MapPlace[],
  context: PublicMapContext,
  options: {
    locale: string;
    selectedPlaceId?: string;
    profilePin?: Artwork | null;
    choosePlace?: (id: string) => void;
    selectStay?: (ids: string[]) => void;
  },
) {
  const bounds = L.latLngBounds([]);
  const placeMarkers = new Map<string, L.Marker>();
  const presenceMarkers = new Map<string, L.Layer>();
  const layers: L.Layer[] = [];
  const icon = (place: MapPlace, selected: boolean) => {
    const pin = document.createElement("span");
    const url =
      place.pinImage?.vectorUrl ??
      place.pinImage?.previewUrl ??
      place.pinImage?.cardUrl ??
      options.profilePin?.vectorUrl ??
      options.profilePin?.previewUrl ??
      options.profilePin?.cardUrl;
    pin.className = `${url ? "city-place-marker-image" : "city-place-marker"}${selected ? " selected" : ""}${place.selectable === false ? " disabled" : ""}`;
    pin.setAttribute("aria-hidden", "true");
    if (
      url &&
      /^(https?:\/\/|\/|data:image\/(?:webp|png|jpeg|svg\+xml)[;,])/i.test(url)
    ) {
      const image = document.createElement("img");
      image.src = url;
      image.alt = "";
      pin.append(image);
    } else pin.append(document.createElement("span"));
    return L.divIcon({
      className: "city-place-marker-shell",
      html: pin,
      iconSize: [42, 50],
      iconAnchor: [21, 48],
    });
  };
  for (const place of places) {
    if (place.latitude === null || place.longitude === null) continue;
    const point = L.latLng(place.latitude, place.longitude);
    bounds.extend(point);
    const choose = options.choosePlace && place.selectable !== false;
    const marker = L.marker(point, {
      icon: icon(place, place.id === options.selectedPlaceId),
      title: `${place.fullName || place.name} — ${place.address}`,
      keyboard: Boolean(choose),
      // Public information must not intercept an offered venue at the same point.
      zIndexOffset: !choose
        ? -1000
        : place.id === options.selectedPlaceId
          ? 1000
          : 0,
    }).addTo(map);
    const label = document.createElement("span");
    label.textContent = place.fullName || place.name;
    marker.bindTooltip(label, { direction: "top", offset: [0, -38] });
    if (choose) {
      marker.on("click", () => options.choosePlace!(place.id));
      marker.on("keydown", (event: L.LeafletKeyboardEvent) => {
        if (!["Enter", " "].includes(event.originalEvent.key)) return;
        L.DomEvent.stop(event.originalEvent);
        options.choosePlace!(place.id);
      });
    }
    placeMarkers.set(place.id, marker);
    layers.push(marker);
  }
  const time = new Intl.DateTimeFormat(
    options.locale === "cs" ? "cs-CZ" : "en-GB",
    {
      timeZone: context.timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    },
  );
  const groups = new Map<string, TimelineItem[]>();
  for (const stay of mergePublicPresence(
    context.timeline ?? [],
    context.stayMergeGapMinutes ?? 5,
  )) {
    if (stay.kind !== "stay" || !stay.point) continue;
    const key = JSON.stringify([
      stay.precision,
      stay.placeId,
      stay.label,
      stay.point,
    ]);
    groups.set(key, [...(groups.get(key) ?? []), stay]);
  }
  for (const group of groups.values()) {
    const stay = group[0]!;
    const point = L.latLng(stay.point!.latitude, stay.point!.longitude);
    bounds.extend(point);
    const name =
      stay.precision === "exact"
        ? stay.label
        : `${options.locale === "cs" ? "Jiné místo" : "Other place"}${stay.label ? ` · ${stay.label}` : ""}`;
    const tooltip = document.createElement("div");
    const heading = document.createElement("strong");
    heading.textContent = name;
    tooltip.append(heading);
    for (const item of group) {
      const interval = document.createElement("span");
      interval.textContent = `${time.format(new Date(item.startsAt))}–${time.format(new Date(item.endsAt))}`;
      tooltip.append(interval);
    }
    if (context.timelineState === "stale") {
      const status = document.createElement("small");
      status.textContent =
        options.locale === "cs"
          ? "Dříve ověřený pobyt"
          : "Previously checked stay";
      tooltip.append(status);
    }
    const place = places.find((p) => p.id === stay.placeId);
    // Reuse a public venue pin; a coarse area never inherits its private address.
    const existing =
      stay.precision === "exact" && stay.placeId
        ? placeMarkers.get(stay.placeId)
        : undefined;
    const marker =
      existing ??
      (stay.precision === "area"
        ? L.circle(point, { radius: 900, color: "#0c087d", fillOpacity: 0.12 })
        : L.marker(point, {
            title: name,
            zIndexOffset: options.choosePlace ? -1000 : 0,
            keyboard: Boolean(options.selectStay),
            icon: icon(
              place ?? {
                id: stay.id,
                name,
                fullName: name,
                address: "",
                latitude: point.lat,
                longitude: point.lng,
              },
              false,
            ),
          })
      ).addTo(map);
    marker.unbindTooltip();
    marker.bindTooltip(tooltip, {
      permanent: true,
      direction: "top",
      offset: stay.precision === "area" ? [0, -12] : [0, -48],
      className: "city-map-tooltip",
    });
    if (!existing) layers.push(marker);
    if (marker instanceof L.Circle) {
      const anchor = () =>
        marker
          .getTooltip()
          ?.setLatLng([marker.getBounds().getNorth(), point.lng]);
      marker.once("remove", () => map.off("zoomend moveend", anchor));
      map.whenReady(() => {
        if (!map.hasLayer(marker)) return;
        map.on("zoomend moveend", anchor);
        anchor();
      });
    }
    if (options.selectStay && (!existing || !options.choosePlace))
      marker.on("click", () =>
        options.selectStay!(group.map((item) => item.id)),
      );
    for (const item of group) presenceMarkers.set(item.id, marker);
  }
  return {
    bounds,
    placeMarkers,
    presenceMarkers,
    dispose: () =>
      layers.forEach((layer) => {
        layer.unbindTooltip();
        if (map.hasLayer(layer)) map.removeLayer(layer);
      }),
  };
}

export function renderCityMapSchedule(
  root: HTMLElement,
  context: PublicMapContext,
  locale: string,
  options: { showStays?: boolean } = {},
) {
  root.replaceChildren();
  const items = mergePublicPresence(
    context.timeline ?? [],
    context.stayMergeGapMinutes ?? 5,
  );
  const sourceStatus = document.createElement("p");
  sourceStatus.className = "city-map-source-status";
  sourceStatus.setAttribute("role", "status");
  sourceStatus.setAttribute(
    "aria-hidden",
    String(context.timelineState !== "stale"),
  );
  sourceStatus.textContent =
    context.timelineState === "stale"
      ? items.length
        ? locale === "cs"
          ? "Dříve ověřené pobyty · přehled se obnovuje."
          : "Previously checked stays · the schedule is refreshing."
        : locale === "cs"
          ? "Přehled pobytů není nyní ověřený. Místa můžete vybírat dál."
          : "The schedule of stays is not currently verified. You can still choose a place."
      : "";
  root.append(sourceStatus);
  if (!items.length || options.showStays === false) {
    return;
  }
  const heading = document.createElement("h3");
  heading.textContent =
    locale === "cs" ? "Kde se budu pohybovat" : "Where I will be";
  root.append(heading);
  const zone = document.createElement("small");
  zone.textContent = context.timeZone;
  root.append(zone);
  const time = new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
    timeZone: context.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const list = document.createElement("ol");
  root.append(list);
  for (const item of items) {
    const row = document.createElement("li");
    row.className = `city-timeline-${item.kind}`;
    const period = document.createElement("span");
    period.className = "city-timeline-time";
    period.textContent = `${time.format(new Date(item.startsAt))}–${time.format(new Date(item.endsAt))}`;
    const label = document.createElement("span");
    label.textContent =
      item.kind === "travel"
        ? locale === "cs"
          ? "Plánovaný přesun"
          : "Planned travel"
        : item.precision === "exact"
          ? `${item.label}${item.address ? ` · ${item.address}` : ""}`
          : `${locale === "cs" ? "Jiné místo" : "Other place"}${item.label ? ` · ${item.label}` : ""}`;
    row.append(period, label);
    list.append(row);
  }
}
